<template>
  <div>
    <PortalTarget name="agreement-popup" />
    <VoucherBanner
      banner-id="voucher_index"
      :banner-index="bannerIndex"
      :pg-name="pgName"
    />

    <div
      v-if="!isLoading"
      :class="'voucher-wrap ' + (device === 'ios' ? 'voucher-ios' : '')"
    >
      <nav v-if="isTabView" class="ticket-tab">
        <ul>
          <li
            v-for="tab in tabList"
            :key="tab.key"
            :class="{ selected: voucherType === tab.key }"
          >
            <a
              href="javascript: void(0)"
              @click.prevent="moveToVoucherTab(tab.key)"
              >{{ tab.title }}</a
            >
          </li>
        </ul>
      </nav>
      <section :class="voucherType">
        <ul class="voucher-list">
          <li
            v-for="(voucher, index) in voucherList"
            :key="`voucher__${index}`"
          >
            <VoucherItem
              :key="`${voucherType}__${index}__${voucher?.quality_name}__item`"
              :voucher="voucher"
              :voucher-type="voucherType"
              :selected-product-id="selectedProductId"
              @on-click-purchase-wavve="onClickPurchase(voucher, $event)"
              @on-click-purchase-partner="purchase(voucher, $event)"
            />
          </li>
        </ul>
      </section>
      <VoucherNotice
        :device="device"
        :voucher-type="voucherType"
        :pg-name="pgName"
      />
    </div>
    <loading-with-props :show-loading="checkActionLoading" />
  </div>
</template>

<script setup>
  import { onMounted, onUnmounted, watch, ref, computed } from 'vue'
  import { useRouter, useRoute } from 'vue-router/composables'
  import { VoucherService } from '@/common/api/api.service'
  import utils from '@/common/util/utils'
  import VoucherBanner from '@/components/lnb/voucher/VoucherBanner'
  import LoadingWithProps from '@/components/LoadingWithProps.vue'
  import {
    DEVICE_TYPE_ANDROID,
    DEVICE_TYPE_IOS,
  } from '@/common/devicetype.const'
  import { PAYMENT_GATE_TYPE_GOOGLE_IN_APP_PURCHASE } from '@/common/constant'
  import braze from '@/common/util/braze'
  import { getPurchaseProperties } from '@/common/util/braze.util'
  import { getCheckPurchaseAction } from '@/common/requests/data-fetch/get-check-purchase-action'
  import { PURCHASE_ACTION_TYPE } from '@/common/normalize'
  import { putUpcomingPayments } from '@/common/requests/data-fetch/put-upcoming-payments'
  import {
    MSG_CHECK_PURCHASE_IN_APP_ALERT,
    MSG_CHECK_PURCHASE_NETWORK_ERROR,
  } from '@/common/util/coupon/const'
  import { confirmChangeUpcomingVoucher } from '@/common/util/voucher'
  import { handleVoucher } from '@/common/util/voucher/handle-voucher'
  import {
    VOUCHER_TICKET_LIST,
    VOUCHER_TYPE_MAP,
    VOUCHER_BANNER_MAP,
    VOUCHER_TYPE,
    WAVVE,
  } from '@/components/voucher/partner/const.js'
  import VoucherItem from '@/components/lnb/voucher/ui/VoucherItem.vue'
  import VoucherNotice from '@/components/voucher/voucher-text/VoucherNotice.vue'

  const router = useRouter()
  const route = useRoute()

  const voucherType = computed(() => route.query['voucher-type'] || 'wavve')
  const device = computed(() => utils.cookies.getDevice() ?? '')
  const pgName = computed(() => utils.getPgName() ?? '')

  const voucherList = ref([])
  const dataList = ref([])
  const isLoading = ref(true)
  const selectedProductId = ref('')
  const checkActionLoading = ref(false)
  const voucherTypeMap = VOUCHER_TYPE_MAP
  const bannerIndex = ref(-1)

  const tabList = ref([])

  const ticketList = computed(() => VOUCHER_TICKET_LIST)

  const isTabView = computed(
    () =>
      utils.cookies.getDevice() !== DEVICE_TYPE_IOS &&
      utils.getPgName() !== PAYMENT_GATE_TYPE_GOOGLE_IN_APP_PURCHASE,
  )

  const { onExternalPurchase, onShowBillingPopup, closePopup, action } =
    handleVoucher()

  const brazeAndRefresh = async (params) => {
    if (params.result === 'y') {
      braze.purchase(getPurchaseProperties(params))
    }
    const credential = utils.cookies.getCredential()
    const isMultiLine = Number(utils.cookies.userInfo('profilecount')) !== 1

    await utils.login.credentialAutoLogin(credential, isMultiLine, false)
    switch (params.next) {
      case 'reload':
        window.location.reload(true)
        break
      case 'setData':
        setData()
        break
      default:
        break
    }
  }

  const postMessage = async (e) => {
    if (e.origin === location.origin) {
      const { eventMessage } = e.data
      if (eventMessage === 'purchaseComplete') {
        await brazeAndRefresh()
      }
    }
  }

  const moveToVoucherTab = (voucherType) => {
    router
      .push(
        `/voucher/index?voucher-type=${voucherType}${pgName.value ? `&pgname=${pgName.value}` : ''}`,
      )
      .catch(() => {})
  }

  const getBannerIndex = (voucherType = '') => {
    if (!voucherType) {
      voucherType = WAVVE
    }

    bannerIndex.value = VOUCHER_BANNER_MAP[voucherType] ?? 0
  }

  const changeUpcomingPayment = async ({ productId }) => {
    const result = await putUpcomingPayments({
      data: {
        product_id: productId,
      },
    })
    if (result) {
      router.replace({ name: 'subscription_ticket' }).catch(() => {})
    }
  }

  const purchase = (voucher, productInfo) => {
    const isExternal =
      voucher?.concurrency?.[0]?.is_product_external?.toUpperCase() === 'Y'

    if (isExternal) {
      return onExternalPurchase(voucher, device.value)
    }

    const actionMap = {
      [VOUCHER_TYPE.WAVVE]: onShowBillingPopup,
    }

    const action = actionMap[voucherType.value] ?? onShowBillingPopup
    return action({
      voucher,
      productInfo,
      device: device.value,
      pgName: pgName.value,
    })
  }

  const onClickPurchase = async (voucher, productInfo) => {
    if (utils.cookies.getCredential() === 'none') {
      window.location.href = `/member/login.html?referer=${encodeURIComponent(
        location.href,
      )}`
      return
    }

    const productId = productInfo?.productid ?? ''
    selectedProductId.value = productId
    checkActionLoading.value = true
    const checkPurchaseResult = await getCheckPurchaseAction({ productId })
    checkActionLoading.value = false
    if (!checkPurchaseResult || !checkPurchaseResult?.data) {
      utils.alert(MSG_CHECK_PURCHASE_NETWORK_ERROR)
      return
    }

    const {
      actionType,
      requestProductInfo,
      requestNonePromotionProductInfo: nonePromotionProductInfo,
    } = checkPurchaseResult.data

    switch (actionType) {
      case PURCHASE_ACTION_TYPE.PAYLETTER_CALL: {
        purchase(voucher, productInfo)
        return
      }
      case PURCHASE_ACTION_TYPE.PUT_UPCOMING_PAYMENTS: {
        const answer = confirmChangeUpcomingVoucher({
          requestProductInfo,
          voucher,
          productInfo,
        })
        if (!answer) return
        await changeUpcomingPayment({ productId })
        return
      }
      case PURCHASE_ACTION_TYPE.SHOW_ALERT: {
        // 알럿창 (인앱결제 불가능 상품)
        utils.alert(MSG_CHECK_PURCHASE_IN_APP_ALERT)
        return
      }

      case PURCHASE_ACTION_TYPE.SHOW_100_PROMOTION_MODAL: {
        await action.promotionModal({
          voucher,
          productInfo,
          device: device.value,
          pgName: pgName.value,
          nonePromotionProductInfo,
        })
        return
      }

      case PURCHASE_ACTION_TYPE.SHOW_100_PROMOTION_CANCEL_NOTI_MODAL: {
        await action.promotionCancelNotificationModal({
          voucher,
          productInfo,
          device: device.value,
          pgName: pgName.value,
        })
        return
      }

      default: {
        // 기타 에러(네트워크 에러 등)
        utils.alert(MSG_CHECK_PURCHASE_NETWORK_ERROR)
      }
    }
  }

  const setTabList = () => {
    const tabs = dataList.value?.map((voucher) => voucher?.passgroup_type ?? '')

    tabList.value = ticketList.value?.filter((tab) => tabs?.includes(tab.key))
  }

  const setData = () => {
    isLoading.value = true

    getBannerIndex(voucherType.value)

    const store =
      device.value === DEVICE_TYPE_ANDROID ? utils.getPgName() : 'wavve'

    VoucherService.getVoucherPurchase({ store, device: device.value }).then(
      ({ data }) => {
        dataList.value =
          typeof data?.data?.json === 'string'
            ? JSON.parse(data.data.json)
            : data.data.json ?? {}

        setTabList()

        const voucherIndex = dataList.value.findIndex(
          (voucher) =>
            voucher.passgroup_type === voucherTypeMap[voucherType.value],
        )
        voucherList.value =
          dataList.value[voucherIndex]?.productgroups?.[0]?.products

        isLoading.value = false
      },
    )
  }

  onMounted(() => {
    window.addEventListener('message', postMessage)
    setData()
  })

  onUnmounted(() => {
    window.removeEventListener('message', postMessage)
  })

  watch(
    () => route.query,
    () => {
      setData()
      closePopup()
    },
  )
</script>
